import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Mirror backend hashlib.sha1(...)[:16]
async function recKey(title, step) {
  const raw = `${(title || "").trim().toLowerCase()}|${(step || "").trim().toLowerCase()}`;
  const buf = new TextEncoder().encode(raw);
  const hash = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

async function pollTask(taskId, { intervalMs = 2500, timeoutMs = 180000 } = {}) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const { data } = await api.get(`/cro/plan-tasks/${taskId}`);
      if (data.status === "done" || data.status === "failed") return data;
    } catch {
      /* keep polling */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Task polling timed out");
}

export default function BulkPlanButton({ shopId, recs, actions }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [progress, setProgress] = useState({ running: false, done: 0, total: 0 });

  const runBulk = async () => {
    if (!recs?.length || progress.running) return;

    // Compute keys for all recs to filter out those already having a plan
    const withKeys = await Promise.all(
      recs.map(async (r) => ({ rec: r, key: await recKey(r.title, r.step) })),
    );
    const pending = withKeys.filter(({ key }) => {
      const existing = actions?.find((a) => a.rec_key === key);
      return !existing?.plan_markdown;
    });

    if (pending.length === 0) {
      toast.info(t("cro_bulk_none_pending"));
      return;
    }

    setProgress({ running: true, done: 0, total: pending.length });
    toast.info(t("cro_bulk_started").replace("{n}", pending.length));

    // Kick off all POSTs in parallel, then poll each in parallel
    const jobs = pending.map(async ({ rec }) => {
      try {
        const { data } = await api.post(`/shops/${shopId}/cro/plan`, {
          title: rec.title,
          step: rec.step,
          problem: rec.problem,
          hypothesis: rec.hypothesis,
          experiment: rec.experiment,
          priority: rec.priority,
          effort: rec.effort,
        });
        await pollTask(data.task_id);
        setProgress((p) => ({ ...p, done: p.done + 1 }));
        qc.invalidateQueries({ queryKey: ["cro-actions", shopId] });
        return { ok: true };
      } catch (e) {
        setProgress((p) => ({ ...p, done: p.done + 1 }));
        return { ok: false, error: e };
      }
    });

    const results = await Promise.all(jobs);
    const okCount = results.filter((r) => r.ok).length;
    const failCount = results.length - okCount;
    setProgress({ running: false, done: 0, total: 0 });
    qc.invalidateQueries({ queryKey: ["cro-actions", shopId] });

    if (failCount === 0) {
      toast.success(t("cro_bulk_done").replace("{n}", okCount));
    } else {
      toast.warning(
        t("cro_bulk_partial")
          .replace("{ok}", okCount)
          .replace("{fail}", failCount),
      );
    }
  };

  const label = progress.running
    ? `${progress.done}/${progress.total} · ${t("cro_bulk_running")}`
    : t("cro_bulk_button");

  const Icon = progress.running
    ? Loader2
    : (recs || []).length === 0
    ? Sparkles
    : (actions || []).filter((a) => a.plan_markdown).length === (recs || []).length
    ? CheckCircle2
    : Sparkles;

  return (
    <Button
      size="sm"
      onClick={runBulk}
      disabled={!recs?.length || progress.running}
      data-testid="cro-bulk-plan-button"
      variant="outline"
      className="gap-1.5"
    >
      <Icon className={progress.running ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
      {label}
    </Button>
  );
}
